import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "@components/ui/Spinner";
import LeadStatusBadge from "@components/sms/LeadStatusBadge";
import ExcludedNumbersManager from "@components/sms/ExcludedNumbersManager";
import Navbar from "@components/layout/Navbar";
import { getSmsLeads, createSmsLead } from "@services/smsLeads";
import { sanitizeText } from "@utils/sanitize";

const STATUSES = [
  { value: "", label: "All Statuses" },
  { value: "IN_SEQUENCE", label: "In Sequence" },
  { value: "REPLIED", label: "Replied" },
  { value: "MANUAL_STOP", label: "Manual Stop" },
  { value: "DO_NOT_SEND", label: "Do Not Send" },
  { value: "NO_REPLY", label: "No Reply" },
  { value: "BLOCKED", label: "Blocked" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function SmsLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState("leads"); // "leads" or "excluded"

  // Add Lead modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [startSequence, setStartSequence] = useState(true);
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSmsLeads(page, perPage, statusFilter, search);
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to fetch SMS leads:", err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, statusFilter, search]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search]);

  const handleAddLead = async () => {
    const phone = sanitizeText(newPhone.trim());
    if (!phone) {
      setAddError("Phone number is required.");
      return;
    }
    setAdding(true);
    setAddError("");
    try {
      await createSmsLead(phone, startSequence);
      setShowAddModal(false);
      setNewPhone("");
      setStartSequence(true);
      fetchLeads();
    } catch (err) {
      setAddError(err.message || "Failed to add lead.");
    } finally {
      setAdding(false);
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar showTabs={false} showFieldSearch={false} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">SMS Leads</h1>
          {activeTab === "leads" && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Add Lead
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("leads")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "leads"
                ? "text-blue-600 border-blue-600"
                : "text-gray-600 border-transparent hover:text-gray-900"
            }`}
          >
            All Leads
          </button>
          <button
            onClick={() => setActiveTab("excluded")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "excluded"
                ? "text-blue-600 border-blue-600"
                : "text-gray-600 border-transparent hover:text-gray-900"
            }`}
          >
            Excluded Numbers
          </button>
        </div>

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            placeholder="Search by phone number..."
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No SMS leads found
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clinic</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Step</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next SMS</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Messages</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Call</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                        lead.unread_count > 0 ? "bg-blue-50 font-semibold" : ""
                      }`}
                      onClick={() => navigate(`/sms-leads/${lead.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          {lead.unread_count > 0 && (
                            <span
                              className="inline-flex items-center justify-center h-5 px-2 text-xs font-bold text-white bg-blue-600 rounded-full"
                              style={{ minWidth: "20px" }}
                            >
                              {lead.unread_count}
                            </span>
                          )}
                          {lead.phone_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {lead.form_submission?.full_name || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {lead.form_submission?.clinic_name || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {lead.form_submission?.email || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <LeadStatusBadge status={lead.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {lead.current_step}/4
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(lead.next_send_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {lead.message_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {lead.conversation_url ? (
                          <a
                            href={lead.conversation_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(lead.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  className={`rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${
                    lead.unread_count > 0 ? "bg-blue-50" : "bg-white"
                  }`}
                  onClick={() => navigate(`/sms-leads/${lead.id}`)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {lead.unread_count > 0 && (
                        <span
                          className="inline-flex items-center justify-center h-5 px-2 text-xs font-bold text-white bg-blue-600 rounded-full"
                          style={{ minWidth: "20px" }}
                        >
                          {lead.unread_count}
                        </span>
                      )}
                      <span className={`text-gray-900 ${lead.unread_count > 0 ? "font-semibold" : "font-medium"}`}>
                        {lead.phone_number}
                      </span>
                    </div>
                    <LeadStatusBadge status={lead.status} />
                  </div>
                  {lead.form_submission && (
                    <div className="text-xs text-gray-700 mb-2">
                      {lead.form_submission.full_name}
                      {lead.form_submission.clinic_name && ` · ${lead.form_submission.clinic_name}`}
                      {lead.form_submission.email && ` · ${lead.form_submission.email}`}
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Step {lead.current_step}/4</span>
                    <span>{lead.message_count || 0} msgs</span>
                    <span>{formatDate(lead.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
          </>
        )}

        {/* Excluded Numbers Tab */}
        {activeTab === "excluded" && <ExcludedNumbersManager />}
      </div>

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Lead</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number (E.164 format)
              </label>
              <input
                type="tel"
                placeholder="+12025551234"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                autoFocus
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={startSequence}
                  onChange={(e) => setStartSequence(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Start SMS sequence immediately
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                If checked, SMS #1 will be sent right away and the 4-step sequence begins.
              </p>
            </div>

            {addError && (
              <p className="text-sm text-red-600 mb-4">{addError}</p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewPhone("");
                  setAddError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLead}
                disabled={adding}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
              >
                {adding ? "Adding..." : "Add Lead"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
